// Minimal SkioBoxChildProducts component
import { LitElement, html, css } from "https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js";

const boxChildStyles = css`
  :host {
    position: relative;
    --skio-qty-primary: #bf563f;
    /* Muted terracotta for disabled +/- (was previous minus default) */
    --skio-qty-disabled-bg: #ead6cf;
    --skio-qty-disabled-border: #ddc2b8;
    --skio-qty-field-border: #d4d4d8;
    --skio-qty-field-text: #1e3a5f;
  }
  .skio-box-child-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem 1.25rem;
    margin: 2rem 0;
    z-index: 0;
  }

  .skio-box-child-card {
    /* border: 1px solid #eee; */
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.03);
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 0.75rem;
  }
  .skio-box-child-card img {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    object-fit: contain;
    border-radius: 8px;
    margin: 0;
  }
  .skio-box-child-card-title {
    font-size: 18px;
  }
  .skio-box-child-card-variant-title {
    font-size: 0.8rem;
  }
  .skio-box-child-card label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 10px;
  }

  .skio-box-child-card button,
  .skio-box-child-card input[type="number"] {
    box-sizing: border-box;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    flex-shrink: 0;
  }

  .skio-box-child-card button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    color: #fff;
    background: var(--skio-qty-primary);
    border: 1px solid var(--skio-qty-primary);
    transition: filter 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  }

  .skio-box-child-card button:hover:not(:disabled) {
    filter: brightness(1.05);
  }

  .skio-box-child-card button:disabled {
    background: var(--skio-qty-disabled-bg);
    border-color: var(--skio-qty-disabled-border);
    color: #fff;
    cursor: not-allowed;
    filter: none;
    opacity: 1;
  }

  .skio-box-child-card input[type="number"] {
    border: 1px solid var(--skio-qty-field-border);
    background: #fff;
    color: var(--skio-qty-field-text);
    padding: 0;
    -moz-appearance: textfield;
  }

  .skio-box-child-card input[type="number"]:focus {
    outline: 2px solid rgba(191, 86, 63, 0.35);
    outline-offset: 1px;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .skio-box-progress-bar-wrapper {
    margin-top: 20px;
    background-color: #fff;
  }
  .skio-box-progress-bar-container {
    width: 100%;
    max-width: 100%;
    margin: 0 auto 1.5rem auto;
    background-color: #fff;
    border: 1px var(--skio-progress-bar-color, #000) solid;
    border-radius: 4px;
    border-radius: 0;
    height: 12px;
    position: relative;
  }
  .skio-box-progress-bar {
    height: 100%;
    background-color: var(--skio-progress-bar-color, #000);
    border-radius: 0;
    transition: width 0.3s;
    opacity: 1;
  }

  .skio-box-progress-status {
    margin: 0.5rem 0 0;
    text-align: center;
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: var(--skio-progress-bar-text-color, #333);
  }

  
`;

export class SkioBoxChildProducts extends LitElement {
  static styles = boxChildStyles;
  static properties = {
    product: { type: Object },
    productHandle: { type: String },
    variantId: { type: String },
    childProducts: { state: true },
    loading: { state: true },
    error: { state: true },
    selectedVariants: { state: true },
    selectedVariant: { type: Object },
    maxQuantity: { state: true },
    options: { type: Object },
  };

  constructor() {
    super();
    this.product = null;
    this.productHandle = '';
    this.variantId = '';
    this.childProducts = [];
    this.loading = false;
    this.error = '';
    this.selectedVariants = {};
    this.selectedVariant = null;
    this.maxQuantity = 0;
    /** Set from get-classic-box-v2; `size` is the allowed total item count per Skio docs */
    this._classicBoxV2 = null;
    /** True only after a successful get-classic-box-v2 response that includes ClassicBoxV2 */
    this._isClassicBoxVariant = false;
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.loading = true;
    this._initSelectedVariantFromAttribute();
    this._initProduct().then(() => {
      this._setupVariantListeners();
      this._setupFormIntegration();
      if (!this.variantId) {
        this.loading = false;
        return;
      }
      return this._fetchChildProducts();
    });
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _initSelectedVariantFromAttribute() {
    // Check if selectedVariant was passed as an attribute from Liquid template
    const selectedVariantAttr = this.getAttribute('selectedVariant');
    if (selectedVariantAttr) {
      try {
        const variant = JSON.parse(selectedVariantAttr);
        if (variant && variant.id) {
          this.selectedVariant = variant;
          this.variantId = variant.id;
        }
      } catch (e) {
        console.warn('Failed to parse selectedVariant attribute:', e);
      }
    }
  }

  async _initProduct() {
    if (this.product) {
      // If product is already provided
      this.variantId = this.product.variants?.[0]?.id || '';
      // Initialize selectedVariant to trigger maxQuantity calculation
      if (this.product.variants?.[0]) {
        this.selectedVariant = this.product.variants[0];
      }
    } else if (this.productHandle) {
      // Fetch product JSON from Shopify
      try {
        const res = await fetch(`/products/${this.productHandle}.js`);
        if (!res.ok) throw new Error('Failed to fetch product');
        this.product = await res.json();
        this.variantId = this.product.variants?.[0]?.id || '';
        // Initialize selectedVariant to trigger maxQuantity calculation
        if (this.product.variants?.[0]) {
          this.selectedVariant = this.product.variants[0];
        }
      } catch (e) {
        this.error = e.message;
      }
    }
    
    // Try to get current variant from form if available
    this._tryGetCurrentVariantFromForm();
  }

  async _fetchChildProducts() {
    if (!this.variantId) return;
    const requestedVariantId = this.variantId;
    this.loading = true;
    this.error = '';
    try {
      const res = await fetch('https://api.skio.com/storefront-http/get-classic-box-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productVariantPlatformId: this.variantId }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (Number(requestedVariantId) !== Number(this.variantId)) return;

      if (!data.ClassicBoxV2) {
        // No classic box payload: normal for non-box SKUs. The API often returns 4xx here.
        if (!res.ok && res.status >= 500) {
          throw new Error('Failed to fetch child products');
        }
        this._isClassicBoxVariant = false;
        this._classicBoxV2 = null;
        this.childProducts = [];
        this.selectedVariants = {};
        this.error = '';
        this._updateMaxQuantity();
        this._injectHiddenInputs();
        return;
      }

      if (!res.ok) throw new Error('Failed to fetch child products');
      this._isClassicBoxVariant = true;
      this._classicBoxV2 = data.ClassicBoxV2;
      this.childProducts = data.ClassicBoxV2.selectableProductVariants || [];

      // Resolve the box size before presetting so we can distribute quantities.
      this._updateMaxQuantity();

      if (this.options?.preset_box && this.childProducts.length > 0) {
        this.selectedVariants = {};
        const childCount = this.childProducts.length;
        // Fall back to one-per-child if the box size is unknown.
        const boxSize = this.maxQuantity > 0 ? this.maxQuantity : childCount;
        const baseQty = Math.floor(boxSize / childCount);
        // Spread any leftover units across the first children so the selected
        // total still equals the box size exactly.
        let remainder = boxSize % childCount;
        this.childProducts.forEach(child => {
          const variantId = child.platformId || child.id;
          let qty = baseQty;
          if (remainder > 0) {
            qty += 1;
            remainder -= 1;
          }
          if (qty > 0) this.selectedVariants[variantId] = qty;
        });
      }

      this._injectHiddenInputs();
    } catch (e) {
      if (Number(requestedVariantId) === Number(this.variantId)) {
        this.error = e.message;
        this.childProducts = [];
        this._classicBoxV2 = null;
        this._isClassicBoxVariant = false;
      }
    } finally {
      if (Number(requestedVariantId) === Number(this.variantId)) {
        this.loading = false;
      }
    }
  }

  _onVariantChange(e) {
    // Shopify's variant:change event (theme dependent)
    const variant = e.detail?.variant;
    if (variant && variant.id && Number(variant.id) !== Number(this.variantId)) {
      this.variantId = variant.id;
      this.selectedVariant = variant;
      this.selectedVariants = {}; // Clear selections
      this._injectHiddenInputs(); // Remove hidden inputs
      this._fetchChildProducts();
    }
  }

  updated(changed) {
    
    if (changed.has('selectedVariant')) {
      this._updateMaxQuantity();
      const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0);
      // Only trim selections when we have a positive cap and exceed it (don't treat max 0 as "clear all")
      if (this.maxQuantity > 0 && totalSelected > this.maxQuantity) {
        this.selectedVariants = {};
        this._injectHiddenInputs();
      }
      setTimeout(() => {
        this._updateAddToCartButtonState();
      }, 800);
    }
    
    this._updateAddToCartButtonState();
  }

  _updateMaxQuantity() {
    const v2 = this._classicBoxV2;
    if (v2 && typeof v2.size === 'number' && v2.size > 0) {
      this.maxQuantity = v2.size;
      return;
    }

    if (this.selectedVariant && this.selectedVariant.title) {
      const numberMatch = this.selectedVariant.title.match(/\d+/);
      let parsed = numberMatch ? parseInt(numberMatch[0], 10) : 0;

      if (this.product?.handle?.includes('office-coffee-bundle-1')) {
        parsed = Math.floor(parsed / 80);
      }

      this.maxQuantity = isNaN(parsed) ? 0 : parsed;
    } else {
      this.maxQuantity = 0;
    }
  }

  _onQuantityChange(e, child) {
    const raw = parseInt(e.target.value, 10);
    const value = Number.isFinite(raw) ? Math.max(0, raw) : 0;
    const variantId = child.platformId || child.id;
    if (value > 0) {
      this.selectedVariants = { ...this.selectedVariants, [variantId]: value };
    } else {
      // Remove if value is 0 or less
      const updated = { ...this.selectedVariants };
      delete updated[variantId];
      this.selectedVariants = updated;
    }
    this._injectHiddenInputs();
  }

  _changeQuantityBy(child, delta) {
    const variantId = child.platformId || child.id;
    const current = this.selectedVariants[variantId] || 0;
    let next = current + delta;
    if (delta > 0 && current === 0) {
      next = 1; // If incrementing from 0, set to 1
    }
    if (next < 0) next = 0;
    // Prevent exceeding max
    const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0) - current + next;
    if (this.maxQuantity && totalSelected > this.maxQuantity) {
      next = current; // Don't allow increment if it would exceed max
    }
    const fakeEvent = { target: { value: next } };
    this._onQuantityChange(fakeEvent, child);
    this._injectHiddenInputs();
  }

  _setupVariantListeners() {
    // Try to find the product form and variant input
    let form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
    let variantInput = form ? form.querySelector('[name="id"]') : document.querySelector('[name="id"]');
    if (variantInput && this.product && Array.isArray(this.product.variants)) {
      variantInput.addEventListener('change', e => {
        const variant = this.product.variants.find(v => v.id == e.target.value);
        if (variant) {
          this.selectedVariant = variant;
          this.variantId = variant.id;
          this.selectedVariants = {};
          this._injectHiddenInputs();
          this._fetchChildProducts();
        }
      });
    }
    // Listen for global variantChanged event (Skio convention)
    document.addEventListener('variantChanged', e => {
      if (!this.product || !Array.isArray(this.product.variants)) return;
      const variantId = e.detail?.variantId;
      const variant = this.product.variants.find(x => x.id == variantId);
      if (variant && Number(variant.id) !== Number(this.variantId)) {
        this.selectedVariant = variant;
        this.variantId = variant.id;
        this.selectedVariants = {};
        this._injectHiddenInputs();
        this._fetchChildProducts();
      }
    });
  }

  _tryGetCurrentVariantFromForm() {
    // Try to get the currently selected variant from the form
    const form = this.findProductForm();
    if (form && this.product && Array.isArray(this.product.variants)) {
      const variantId = this.getVariantId(form);
      if (variantId) {
        const variant = this.product.variants.find(v => v.id == variantId);
        if (variant) {
          this.selectedVariant = variant;
          this.variantId = variantId;
        }
      }
    }
  }

  _setupFormIntegration() {
    // Always use the main product form for theme-agnostic support
    this._form = document.querySelector('form[action*="/cart/add"]');
  }

  findProductForm() {
    // First try finding form through parent elements
    const formFromParent = this.findFormThroughParents();
    if (formFromParent) return formFromParent;
    // If no form found through parents, look for any valid product forms on page
    const allProductForms = this.getAllProductForms();
    return allProductForms[0] || null;
  }

  findFormThroughParents() {
    let currentElement = this;
    let depth = 0;
    const maxDepth = 5;
    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'));
      const validForm = forms.find(form => this.isValidProductForm(form));
      if (validForm) return validForm;
      currentElement = currentElement.parentElement;
      depth++;
    }
    return null;
  }

  getAllProductForms() {
    return Array.from(document.forms).filter(form => {
      const hasVariantId = this.getVariantId(form);
      return this.isValidProductForm(form) && hasVariantId;
    });
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add');
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]');
    return hasCartAction && hasSubmitButton;
  }

  getVariantId(form) {
    // Try getting ID from FormData first
    const formData = new FormData(form);
    const idFromFormData = formData.get('id');
    if (idFromFormData) return Number(idFromFormData);
    // Fallback to finding input element
    const idInput = form.querySelector('[name="id"]');
    return idInput ? Number(idInput.value) : null;
  }
 
  _collectClassicBoxLineProperties() {
    const props = {};
    let any = false;
    for (const [variantId, qty] of Object.entries(this.selectedVariants)) {
      if (qty <= 0) continue;
      any = true;
      props[`_pv${variantId}`] = String(qty);
      const child = this.childProducts.find(c => (c.platformId || c.id) == variantId);
      if (child?.productTitle) props[child.productTitle] = String(qty);
    }
    if (any) props._isClassicBoxV2 = 'true';
    return props;
  }

  /**
   * Subscription PDP uses /cart/clear?return_to=/cart/add?items[]... (no product form).
   * Line item properties must be merged into that inner /cart/add URL as items[][properties][key].
   */
  _syncSubscriptionPdpPermalink() {
    const ctx = this.closest('[data-subscription-pdp-checkout-context]');
    if (!ctx) return;
    const anchor = ctx.querySelector('a.product-checkout-btn');
    if (!anchor) return;
    const props = this._collectClassicBoxLineProperties();
    if (Object.keys(props).length === 0) {
      delete anchor.dataset.skioClassicBoxProperties;
    } else {
      anchor.dataset.skioClassicBoxProperties = JSON.stringify(props);
    }
    if (typeof window.skioRebuildSubscriptionPdpCheckout === 'function') {
      window.skioRebuildSubscriptionPdpCheckout(anchor);
    }
  }

  _injectHiddenInputs() {
    const form = this.findProductForm();
    if (form) {
      Array.from(form.querySelectorAll('input[data-skio-child-variant]')).forEach(input => input.remove());
      for (const [variantId, qty] of Object.entries(this.selectedVariants)) {
        if (qty > 0) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[_pv${variantId}]`;
          input.value = qty;
          input.setAttribute('data-skio-child-variant', 'true');
          form.appendChild(input);
          const child = this.childProducts.find(c => (c.platformId || c.id) == variantId);
          if (child) {
            const titleInput = document.createElement('input');
            titleInput.type = 'hidden';
            titleInput.name = `properties[${child.productTitle}]`;
            titleInput.value = qty;
            titleInput.setAttribute('data-skio-child-variant', 'true');
            form.appendChild(titleInput);
          }
        }
      }
    }
    this._syncSubscriptionPdpPermalink();
    this._updateAddToCartButtonState();
  }

  _checkoutCompleteForClassicBox() {
    if (this.options?.preset_box) return true;
    if (this.loading) return false;
    if (!this._isClassicBoxVariant) return true;
    const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0);
    return !!(this.maxQuantity && totalSelected === this.maxQuantity);
  }

  _updateAddToCartButtonState() {
    const shouldEnable = this._checkoutCompleteForClassicBox();

    const form = this.findProductForm();
    if (form) {
      const addToCartBtn = form.querySelector('button[type="submit"], [type="submit"]');
      if (addToCartBtn) {
        if (this.options?.preset_box) {
          addToCartBtn.disabled = false;
        } else {
          addToCartBtn.disabled = !shouldEnable;
        }
      }
    }

    const ctx = this.closest('[data-subscription-pdp-checkout-context]');
    const checkout = ctx?.querySelector('a.product-checkout-btn');
    if (checkout) {
      if (this.options?.preset_box) {
        checkout.classList.remove('tw-pointer-events-none', 'tw-opacity-50');
        checkout.setAttribute('aria-disabled', 'false');
      } else {
        checkout.classList.toggle('tw-pointer-events-none', !shouldEnable);
        checkout.classList.toggle('tw-opacity-50', !shouldEnable);
        checkout.setAttribute('aria-disabled', shouldEnable ? 'false' : 'true');
      }
    }
  }

  render() {
    // If preset_box is true, don't show the UI
    if (this.options?.preset_box) {
      return html``;
    }

    if (this.loading) {
      return html`<div style="text-align:center;padding:1rem;">Loading options...</div>`;
    }

    if (this.error) {
      return html`<div style="text-align:center;padding:1rem;color:#b00020;">${this.error}</div>`;
    }

    if (!this.childProducts.length) {
      return html``;
    }

    const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0);
    const max = this.maxQuantity;
    const percent = max ? Math.min(100, Math.round((totalSelected / max) * 100)) : 0;
    const statusText =
      max > 0
        ? totalSelected < max
          ? `Add ${max - totalSelected} more to continue`
          : 'Ready to add to cart'
        : 'Select a valid box variant';

    return html`
      <div class="skio-box-progress-bar-wrapper">
        <div class="skio-box-progress-bar-container">
          <div
            class="skio-box-progress-bar"
            style="width: ${percent}%;"
            title="Select all required items to continue"
          ></div>
        </div>
        <p class="skio-box-progress-status">${statusText}</p>
      </div>
      <div class="skio-box-child-grid">
        ${this.childProducts.map(child => {
          const variantId = child.platformId || child.id;
          const quantity = this.selectedVariants[variantId] || 0;
          const disableUp = max && totalSelected >= max;
          return html`
            <div class="skio-box-child-card">
              <img src="${child.imageSrc}" alt="${child.productTitle}" />
              <div class="skio-box-child-card-title-container">
                <div class="skio-box-child-card-title">${child.productTitle}</div>
                ${child.productVariantTitle && !child.productVariantTitle.includes('Default Title') ? html`<div class="skio-box-child-card-variant-title">${child.productVariantTitle}</div>` : ''}
              </div>
              <div>
                <label>
                  <button type="button" @click="${() => this._changeQuantityBy(child, -1)}">-</button>
                  <input type="number" min="0" step="1" .value="${quantity}" @input="${e => this._onQuantityChange(e, child)}" @change="${e => this._onQuantityChange(e, child)}" />
                  <button type="button" @click="${() => this._changeQuantityBy(child, 1)}" ?disabled="${disableUp}">+</button>
                </label>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }
}

customElements.define('skio-static-build-a-box', SkioBoxChildProducts);